import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/providers/AuthProvider";
import axios from "axios";

import { anyService } from "@/services/breeze/AnyService";
import { entityService } from "@/services/breeze/EntityService";
import { formatAnyName, singularize } from "@/utils/text";

import Tabs from "@/components/ui/Tabs";
import { SkeletonPage } from "@/components/ui/Skeleton";
import { EntityWorkflows } from "@/components/entity/EntityWorkflows";
import EntityRlsUsers from "@/components/entity/EntityRlsUsers";
import EntityRlsGroups from "@/components/entity/EntityRlsGroups";
import NotFound from "@/components/ui/NotFound";
import ErrorCard from "@/components/ui/ErrorCard";
import EntityFormV2, {
  EntityFormHandle,
} from "@/components/entity/EntityFormV2";
import PageTemplate from "@/components/page-template/PageTemplate";
import { Button, Flex, Box, Heading } from "@radix-ui/themes";
import { Check, X } from "lucide-react";

interface EntityDetailViewProps {
  entityName: string;
  entityId: number;
  initialTab?: string;
  isPage?: boolean;
}

export default function EntityDetailView({
  entityName,
  entityId,
  initialTab,
  isPage = false,
}: EntityDetailViewProps) {
  const formRef = useRef<EntityFormHandle>(null);
  const queryClient = useQueryClient();
  const { isTenantAdmin, currentTenant } = useAuth();
  const showGroupsPanel =
    currentTenant?.tenant_settings?.enable_group_rls === true;
  const [mode, setMode] = useState<"view" | "edit">("view");

  const {
    data: entityDefinition,
    isLoading: isEntityDefinitionLoading,
    error: isEntityDefinitionError,
  } = useQuery({
    queryKey: ["entity", entityName],
    queryFn: () => entityService.getByName(entityName),
  });

  const {
    data: entityData,
    isLoading: isEntityDataLoading,
    error: isEntityDataError,
  } = useQuery({
    queryKey: ["entity-data", entityName, entityId],
    queryFn: async () => anyService.getById(entityName, entityId, ["*"]),
    retry: false,
    retryOnMount: false,
  });

  const { data: permissions, isLoading: isPermissionsLoading } = useQuery({
    queryKey: ["entity-permissions", entityName, entityId],
    queryFn: () => anyService.getRecordLevelPermissions(entityName, entityId),
  });

  const handleSaveClick = () => {
    formRef.current?.saveChanges((cb?: () => void) => {
      setMode("view");
      cb?.();
    });
  };

  const handleCancelClick = () => {
    formRef.current?.cancelChanges((cb?: () => void) => {
      setMode("view");
      cb?.();
    });
  };

  const handleLockClick = () => {
    if (entityData) {
      anyService
        .updateLockedStatus(entityName, entityId, !entityData.locked)
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["entity-data", entityName, entityId],
          });
        });
    }
  };

  if (isEntityDefinitionLoading || isEntityDataLoading || isPermissionsLoading) {
    return <SkeletonPage />;
  }

  const isNotFoundError =
    isEntityDataError &&
    axios.isAxiosError(isEntityDataError) &&
    isEntityDataError.response?.status === 404;

  if (isNotFoundError) {
    return <NotFound />;
  } else if (isEntityDefinitionError || isEntityDataError) {
    return (
      <ErrorCard
        error={(isEntityDefinitionError || isEntityDataError)?.message}
      />
    );
  }

  if (!entityDefinition || !entityData) {
    return <ErrorCard error="Missing entity definition or data" />;
  }

  const actionBar = (
    <>
      {permissions?.update && (
        <div className="flex gap-2">
          <Button
            variant="soft"
            color={entityData.locked ? "red" : "green"}
            onClick={handleLockClick}
          >
            {entityData.locked ? "Unlock Record" : "Lock Record"}
          </Button>
          {!entityData.locked && (
            <>
              |
              {mode === "view" ? (
                <Button variant="soft" onClick={() => setMode("edit")}>
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="soft"
                    color="red"
                    onClick={handleCancelClick}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    variant="soft"
                    color="green"
                    onClick={handleSaveClick}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );

  const tabsContent = (
    <Tabs
      items={[
        {
          key: "details",
          label: "Details",
          content: (
            <EntityFormV2
              ref={formRef}
              entity={entityDefinition}
              initialData={entityData}
              mode={mode}
              userHasUpdatePermission={permissions?.update ?? false}
              splitFieldsIntoGrid={isPage}
            />
          ),
        },
        {
          key: "workflows",
          label: "Workflows",
          content: (
            <EntityWorkflows entityName={entityName} entityId={entityId} />
          ),
        },
        {
          key: "rls",
          label: "Row-Level Security",
          content: (
            <Box>
              <Heading size="3" mb="3">
                Users
              </Heading>
              <EntityRlsUsers
                entityName={entityName}
                entityId={entityData.id}
              />
              {showGroupsPanel && (
                <Box mt="6">
                  <Heading size="3" mb="3">
                    Groups
                  </Heading>
                  <EntityRlsGroups
                    entityName={entityName}
                    entityId={entityData.id}
                  />
                </Box>
              )}
            </Box>
          ),
          hidden: !(entityDefinition.enable_rls && isTenantAdmin),
        },
      ]}
      selectedTab={initialTab}
    />
  );

  if (isPage) {
    return (
      <PageTemplate
        title={`${singularize(formatAnyName(entityName))} Details`}
        rightContent={<Flex className="mt-2">{actionBar}</Flex>}
      >
        {tabsContent}
      </PageTemplate>
    );
  }

  return (
    <Box>
      <Flex justify="end" align="center" mb="4">
        {actionBar}
      </Flex>
      {tabsContent}
    </Box>
  );
}
